export const slugifyHeading = (text: string): string =>
  text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// ── TOC line helpers ─────────────────────────────────────────

// trailing page number: dot leaders ("....", "…"), or 2+ spaces/nbsp/tab, then digits
const TRAILING_PAGE = /(?:\s*[.\u2026](?:\s*[.\u2026])+|\s{2,}|\t)\s*\d+\s*$/;

/** "1. Intro ..... 3", "Intro … 3", or "1. Intro    3" */
export const isTOCEntry = (text: string): boolean => {
  const t = text.trim();
  if (!t || t.length > 200) return false;
  const withLeader = /^\S.*?[.\u2026](?:\s*[.\u2026])+\s*\d+$/.test(t);
  const numberedWithGap = /^\d+(?:\.\d+)*\.?\s+\S.*?\s{2,}\d+$/.test(t);
  return withLeader || numberedWithGap;
};

export const extractTOCTitle = (text: string): string =>
  text
    .replace(/^\s*\d+(?:\.\d+)*\.?\s*/, "") // leading "1." / "2.1"
    .replace(TRAILING_PAGE, "")
    .trim();

/** Tolerant comparison key: ignores case, accents, punctuation, "&" vs "and". */
export const normalizeTitle = (s: string): string =>
  extractTOCTitle(s)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .trim();

const sectionNumber = (text: string): string | null => {
  const m = text.match(/^\s*(\d+(?:\.\d+)*)\.?(?:\s|$)/);
  return m ? m[1] : null;
};

/**
 * Matches a generic "Table of Contents" heading line, regardless of report
 * type. Tolerant of the singular/plural variants Word documents commonly
 * use ("Table of Content" vs "Table of Contents", "Content" vs
 * "Contents"), optional surrounding punctuation, and non-breaking spaces.
 */
const TOC_TITLE_PATTERN = /^(table\s+of\s+contents?|contents?|toc)\s*:?\s*$/i;

export const isTocTitleText = (text: string): boolean =>
  TOC_TITLE_PATTERN.test(text.trim());

// ── Fixed report section boundaries ─────────────────────────
// This report template always starts its 2nd page with a "Report Details"
// heading, and always starts a fresh page at "Table of Contents" (never
// sharing a page with whatever precedes it, e.g. a "Document History"
// table). These two headings are therefore treated as mandatory page
// starts — a stronger rule than ordinary height-based pagination, used on
// top of it so the cover page never bleeds into the report-details
// section and the report-details/approval/disclaimer section never bleeds
// into the TOC page, regardless of how much room is left on the page.
const FORCED_PAGE_START_PATTERN = /^report\s+details\s*:?\s*$/i;

export const isForcedPageStartText = (text: string): boolean =>
  FORCED_PAGE_START_PATTERN.test(text.trim()) || isTocTitleText(text);

/**
 * Given the plain-text content of a document's top-level blocks (in order),
 * returns the [start, end] (inclusive) index ranges that make up a Table of
 * Contents block: a title line ("Table of Contents" / "Contents") followed
 * by its contiguous TOC entry lines (blank spacer paragraphs in between are
 * tolerated and skipped). Detection is strictly anchored to that title —
 * only the run of entries immediately following it counts as the TOC — so
 * unrelated content elsewhere in the document (e.g. a Summary section, or
 * any other paragraph that happens to resemble the dot-leader/page-number
 * pattern) is never mistaken for part of the TOC. The scan stops at the
 * first real, non-blank line that isn't itself a TOC entry.
 *
 * Both the Navigation Panel's TOC extraction and the pagination "keep
 * together" logic share this single definition of "what counts as the
 * TOC", so the two stay consistent. Works for any report type since it
 * only relies on the generic title/entry text patterns, not on any
 * hardcoded section names.
 */
export const findTocBlockRanges = (
  texts: string[],
): Array<{ start: number; end: number }> => {
  const ranges: Array<{ start: number; end: number }> = [];
  let i = 0;
  while (i < texts.length) {
    if (isTocTitleText(texts[i])) {
      const titleIndex = i;
      let end = titleIndex;
      let cursor = titleIndex + 1;

      while (cursor < texts.length) {
        const text = texts[cursor];
        if (!text) {
          // Blank spacer paragraph — skip without breaking the run.
          cursor += 1;
          continue;
        }
        if (isTOCEntry(text)) {
          end = cursor;
          cursor += 1;
          continue;
        }
        // First real, non-matching content (e.g. the next section's
        // heading/body) — the TOC block stops here.
        break;
      }

      if (end > titleIndex) {
        ranges.push({ start: titleIndex, end });
      }
      i = Math.max(end, titleIndex) + 1;
    } else {
      i += 1;
    }
  }
  return ranges;
};

// ── Scrolling ────────────────────────────────────────────────

const PAGE_ROOT = ".docgen-page-content .ProseMirror"; // excludes header/footer
const HEADING_SELECTOR = [1, 2, 3, 4, 5, 6]
  .map((n) => `${PAGE_ROOT} h${n}`)
  .join(", ");

function scrollElementIntoCanvas(el: HTMLElement) {
  const canvas = document.querySelector(
    ".docgen-editor-canvas",
  ) as HTMLElement | null;

  if (!canvas) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  const top =
    canvas.scrollTop +
    (el.getBoundingClientRect().top - canvas.getBoundingClientRect().top) -
    30;

  canvas.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

/** By heading DOM id (works across all pages). */
export function scrollToHeading(id: string): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  scrollElementIntoCanvas(el);
  return true;
}

function findHeadingElement(
  tocText: string,
  occurrence: number,
): HTMLElement | null {
  const key = normalizeTitle(tocText);
  if (!key) return null;
  const num = sectionNumber(tocText);

  const hs = Array.from(
    document.querySelectorAll<HTMLElement>(HEADING_SELECTOR),
  );
  // Some content renders headings as plain paragraphs
  const ps = Array.from(
    document.querySelectorAll<HTMLElement>(`${PAGE_ROOT} p`),
  ).filter((p) => {
    const t = (p.textContent || "").trim();
    return t.length > 0 && t.length <= 150 && !isTOCEntry(t);
  });

  const keyOf = (el: HTMLElement) => normalizeTitle(el.textContent || "");
  const exact = (el: HTMLElement) => keyOf(el) === key;
  const sameNumber = (el: HTMLElement) =>
    num !== null && sectionNumber(el.textContent || "") === num;
  const loose = (el: HTMLElement) => {
    const k = keyOf(el);
    return (
      k.length >= 4 && key.length >= 4 && (k.startsWith(key) || key.startsWith(k))
    );
  };

  // Strictest strategy first; the first one that finds anything wins.
  const attempts: HTMLElement[][] = [
    hs.filter(exact),
    ps.filter(exact),
    hs.filter(sameNumber),
    hs.filter(loose),
    ps.filter((p) => loose(p) && Math.abs(keyOf(p).length - key.length) <= 15),
  ];

  for (const found of attempts) {
    if (found.length) return found[occurrence] ?? found[0];
  }
  return null;
}

/** By TOC text or title. `occurrence` picks the Nth match when titles repeat. */
export function scrollToHeadingByTitle(title: string, occurrence = 0): boolean {
  const el = findHeadingElement(title, occurrence);

  if (!el) {
    console.warn("[TOC] No heading found", {
      tocText: title,
      key: normalizeTitle(title),
      headingsOnPage: Array.from(
        document.querySelectorAll<HTMLElement>(HEADING_SELECTOR),
      ).map((h) => normalizeTitle(h.textContent || "")),
    });
    return false;
  }

  scrollElementIntoCanvas(el);
  return true;
}

/** Click on a TOC line that lives inside the document body. */
export function scrollFromTocLine(line: HTMLElement): boolean {
  const text = (line.textContent || "").trim();
  if (!isTOCEntry(text)) return false;

  const key = normalizeTitle(text);
  const sameTitleLines = Array.from(
    document.querySelectorAll<HTMLElement>(`${PAGE_ROOT} p`),
  ).filter((p) => {
    const t = (p.textContent || "").trim();
    return isTOCEntry(t) && normalizeTitle(t) === key;
  });

  return scrollToHeadingByTitle(
    text,
    Math.max(0, sameTitleLines.indexOf(line)),
  );
}